import { useState, type ReactNode } from 'react'
import { useFeesReport, useValueReport, useTopProducts } from '@/hooks/useReports'
import { downloadReportXlsx, type TopSort } from '@/lib/api/reports'
import { statusBadge } from '@/lib/active-status'
import {
  formatMoney,
  formatPercent,
  computeDelta,
  deltaClass,
  deltaArrow,
  signalPill,
} from '@/lib/report-format'
import type { ValuePeriod } from '@/lib/schemas/reports'

const TH =
  'px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase whitespace-nowrap'

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(d)
}

export default function Reports() {
  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold">Reports</h1>
      <FeeReconciliationPanel />
      <ValueSummaryPanel />
      <TopProductsPanel />
    </div>
  )
}

// ---- Shared panel primitives ----

function PanelCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4 border border-gray-200 p-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </section>
  )
}

function DateRange({
  start,
  end,
  onStart,
  onEnd,
  onReset,
  resetLabel,
  idPrefix,
}: {
  start: string
  end: string
  onStart: (v: string) => void
  onEnd: (v: string) => void
  onReset: () => void
  resetLabel: string
  idPrefix: string
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-start`} className="block text-xs text-gray-500">
          From
        </label>
        <input
          id={`${idPrefix}-start`}
          type="date"
          value={start}
          onChange={(e) => onStart(e.target.value)}
          className="border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-end`} className="block text-xs text-gray-500">
          To
        </label>
        <input
          id={`${idPrefix}-end`}
          type="date"
          value={end}
          onChange={(e) => onEnd(e.target.value)}
          className="border border-gray-300 px-2 py-1 text-sm"
        />
      </div>
      <button
        type="button"
        onClick={onReset}
        className="border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-black"
      >
        {resetLabel}
      </button>
    </div>
  )
}

function ExportButton({ onExport }: { onExport: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function click() {
    setBusy(true)
    setError(null)
    try {
      await onExport()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => void click()}
        disabled={busy}
        className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? 'Exporting…' : 'Export to Excel'}
      </button>
      {error && (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}

function PanelState({
  isLoading,
  isError,
  error,
  isEmpty,
  emptyLabel,
  onRetry,
  children,
}: {
  isLoading: boolean
  isError: boolean
  error: unknown
  isEmpty: boolean
  emptyLabel: string
  onRetry: () => void
  children: ReactNode
}) {
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>
  if (isError) {
    return (
      <div className="space-y-2">
        <p role="alert" className="text-sm text-red-600">
          {error instanceof Error ? error.message : 'Failed to load.'}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
        >
          Retry
        </button>
      </div>
    )
  }
  if (isEmpty) return <p className="text-sm text-gray-500">{emptyLabel}</p>
  return <>{children}</>
}

function PeriodCaption({ start, end }: { start?: string; end?: string }) {
  if (!start || !end) return null
  return (
    <p className="text-xs text-gray-500">
      Showing {start} – {end}
    </p>
  )
}

// ---- Panel 1: Weekly fee reconciliation ----

function FeeReconciliationPanel() {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const query = useFeesReport({ start, end })
  const data = query.data

  return (
    <PanelCard title="Weekly fee reconciliation">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRange
          idPrefix="fees"
          start={start}
          end={end}
          onStart={setStart}
          onEnd={setEnd}
          onReset={() => {
            setStart('')
            setEnd('')
          }}
          resetLabel="This week"
        />
        <ExportButton onExport={() => downloadReportXlsx('fees', { start, end })} />
      </div>

      <p className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        ⚠️ KH's weekly invoice includes both the $31.99 fee AND customs charges. Verify this fee
        count matches KH's invoice line count, then accept the customs total from KH separately.
      </p>

      <PanelState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={!!data && data.lines.length === 0}
        emptyLabel="No dropship orders in this period"
        onRetry={() => void query.refetch()}
      >
        {data && (
          <>
            <p className="text-sm">
              <span className="font-semibold tabular-nums">{data.order_count}</span> orders ·{' '}
              <span className="font-semibold tabular-nums">{formatMoney(data.total_fees)}</span>{' '}
              total fees
            </p>
            <PeriodCaption start={data.start} end={data.end} />
            <div className="overflow-x-auto border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={TH}>Order</th>
                    <th className={TH}>Customer</th>
                    <th className={TH}>Items sent</th>
                    <th className={TH}>Submitted</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Wholesale</th>
                    <th className={TH}>Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((line) => {
                    const badge = statusBadge(line.status)
                    return (
                      <tr key={line.order_number} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-3 py-2 font-medium tabular-nums">#{line.order_number}</td>
                        <td className="px-3 py-2">{line.customer_name}</td>
                        <td className="px-3 py-2 tabular-nums">{line.items_sent}</td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                          {formatDateTime(line.submitted_at)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block border px-2 py-0.5 text-xs ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                          {formatMoney(line.wholesale_value)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                          {formatMoney(line.dropship_fee)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </PanelState>
    </PanelCard>
  )
}

// ---- Panel 2: Dropship value summary ----

type MetricKind = 'money' | 'percent' | 'count'

interface MetricDef {
  key: keyof ValuePeriod
  label: string
  kind: MetricKind
  lowerIsBetter?: boolean
}

const VALUE_METRICS: MetricDef[] = [
  { key: 'retail_value', label: 'Total retail value', kind: 'money' },
  { key: 'wholesale_value', label: 'Total wholesale value', kind: 'money' },
  { key: 'total_fees', label: 'Total fees', kind: 'money', lowerIsBetter: true },
  { key: 'gross_margin_pct', label: 'Estimated gross margin', kind: 'percent' },
  { key: 'order_count', label: 'Order count', kind: 'count' },
]

function metricValue(period: ValuePeriod, def: MetricDef): string {
  const raw = period[def.key]
  if (def.kind === 'money') return formatMoney(raw as string)
  if (def.kind === 'percent') return formatPercent(raw as string | null)
  return String(raw)
}

function MetricDelta({
  current,
  previous,
  def,
}: {
  current: ValuePeriod
  previous: ValuePeriod
  def: MetricDef
}) {
  if (def.kind === 'count') {
    const cur = current[def.key] as number
    const prev = previous[def.key] as number
    const diff = cur - prev
    const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat'
    return (
      <p className={`text-xs ${deltaClass(direction, def.lowerIsBetter)}`}>
        {deltaArrow(direction)} {Math.abs(diff)} vs {prev} prior
      </p>
    )
  }

  const curRaw = current[def.key] as string | null
  const prevRaw = previous[def.key] as string | null
  if (curRaw == null || prevRaw == null) {
    return <p className="text-xs text-gray-400">— vs {formatPercent(prevRaw)} prior</p>
  }

  const delta = computeDelta(curRaw, prevRaw)
  const magnitude =
    def.kind === 'money' ? formatMoney(delta.amount) : `${delta.amount} pts`
  const priorLabel = def.kind === 'money' ? formatMoney(prevRaw) : formatPercent(prevRaw)
  return (
    <p className={`text-xs ${deltaClass(delta.direction, def.lowerIsBetter)}`}>
      {deltaArrow(delta.direction)} {magnitude} vs {priorLabel} prior
    </p>
  )
}

function ValueSummaryPanel() {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [compare, setCompare] = useState(false)
  const query = useValueReport({ start, end, compare })
  const data = query.data

  return (
    <PanelCard title="Dropship value summary">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRange
          idPrefix="value"
          start={start}
          end={end}
          onStart={setStart}
          onEnd={setEnd}
          onReset={() => {
            setStart('')
            setEnd('')
          }}
          resetLabel="This month"
        />
        <ExportButton onExport={() => downloadReportXlsx('value', { start, end })} />
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
        <span>Compare to prior period</span>
      </label>

      <PanelState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={false}
        emptyLabel=""
        onRetry={() => void query.refetch()}
      >
        {data && (
          <>
            <PeriodCaption start={data.current.start} end={data.current.end} />
            {compare && !data.previous && (
              <p className="text-xs text-gray-500">No prior period to compare.</p>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {VALUE_METRICS.map((def) => (
                <div key={def.key} className="border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">{def.label}</div>
                  <div className="mt-1 text-lg font-semibold tabular-nums">
                    {metricValue(data.current, def)}
                  </div>
                  {compare && data.previous && (
                    <MetricDelta current={data.current} previous={data.previous} def={def} />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </PanelState>
    </PanelCard>
  )
}

// ---- Panel 3: Top products dropshipped ----

const SORTS: { key: TopSort; label: string }[] = [
  { key: 'units', label: 'Units' },
  { key: 'orders', label: 'Orders' },
  { key: 'value', label: 'Retail value' },
]

function TopProductsPanel() {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [sort, setSort] = useState<TopSort>('units')
  const query = useTopProducts({ start, end, sort })
  const data = query.data

  return (
    <PanelCard title="Top products dropshipped">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <DateRange
          idPrefix="top"
          start={start}
          end={end}
          onStart={setStart}
          onEnd={setEnd}
          onReset={() => {
            setStart('')
            setEnd('')
          }}
          resetLabel="Last 90 days"
        />
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label htmlFor="top-sort" className="block text-xs text-gray-500">
              Sort by
            </label>
            <select
              id="top-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as TopSort)}
              className="border border-gray-300 px-2 py-1 text-sm"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <ExportButton onExport={() => downloadReportXlsx('top-products', { start, end, sort })} />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Repeated products may be worth stocking in Brewster to eliminate the $31.99 fee and improve
        margin.
      </p>

      <PanelState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isEmpty={!!data && data.rows.length === 0}
        emptyLabel="No dropshipped products in this period"
        onRetry={() => void query.refetch()}
      >
        {data && (
          <>
            <PeriodCaption start={data.start} end={data.end} />
            <div className="overflow-x-auto border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={TH}>Product</th>
                    <th className={TH}>SKU</th>
                    <th className={TH}>Units</th>
                    <th className={TH}>Orders</th>
                    <th className={TH}>Total retail value</th>
                    <th className={TH}>Total wholesale value</th>
                    <th className={TH}>Stock signal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const pill = signalPill(row.signal)
                    return (
                      <tr key={row.sku} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-3 py-2 font-medium">{row.title}</td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums text-gray-600">
                          {row.sku}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{row.units}</td>
                        <td className="px-3 py-2 tabular-nums">{row.orders}</td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                          {formatMoney(row.retail_value)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                          {formatMoney(row.wholesale_value)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block border px-2 py-0.5 text-xs ${pill.className}`}>
                            {pill.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </PanelState>
    </PanelCard>
  )
}
